import { supabase } from '@/lib/supabase';

// ─────────────────────────────────────────────────────────────
// ZIFEK — Statistiques de l'Assistant IA (chat client)
// Chaque interaction produit dans le chat IA est journalisée afin que le
// marchand concerné retrouve, dans son dashboard, les statistiques de
// fréquentation issues du chat IA (et pas seulement de son site public).
// La source est explicite : « Assistant IA ZIFEK ».
// ─────────────────────────────────────────────────────────────

export type AiChatEventType = 'display' | 'click' | 'cart' | 'boutique';

export interface AiChatProductEventInput {
  /** Id du client qui effectue la recherche (l'utilisateur connecté). */
  clientUserId?: number | null;
  /** Nom du client (affichage marchand). */
  clientName?: string | null;
  /**
   * Type d'événement :
   *  - `display` : le contenu apparaît dans les résultats du chat IA ;
   *  - `click`   : le client ouvre la fiche du contenu ;
   *  - `cart`    : le client ajoute le contenu au panier ;
   *  - `boutique`: le client ouvre la FICHE BOUTIQUE d'un vendeur via l'IA.
   */
  eventType: AiChatEventType;
  /** Id du produit concerné. */
  productId?: number | null;
  /** Nom du produit (snapshot). */
  productName?: string | null;
  /** Id du marchand (boutique) propriétaire du produit. */
  merchantId?: number | null;
  /** Requête / mots-clés ayant conduit à l'affichage. */
  query?: string | null;
}

/**
 * Enregistre un ou plusieurs événements d'interaction produit du chat IA.
 * Écriture best-effort : ne bloque jamais l'expérience du chat.
 */
export async function trackAiChatEvents(events: AiChatProductEventInput[]): Promise<void> {
  const rows = (events || [])
    // On accepte les événements liés à un produit OU à une boutique (un
    // vendeur doit aussi savoir quand un client a ouvert SA fiche via l'IA).
    .filter((e) => e && (e.productId || (e.eventType === 'boutique' && e.merchantId)))
    .map((e) => ({
      client_user_id: e.clientUserId ?? null,
      client_name: e.clientName ? String(e.clientName).slice(0, 120) : null,
      event_type: e.eventType,
      product_id: e.productId ?? null,
      product_name: e.productName ? String(e.productName).slice(0, 200) : (e.eventType === 'boutique' ? 'Boutique' : null),
      merchant_id: e.merchantId ?? null,
      query: e.query ? String(e.query).slice(0, 500) : null,
    }));

  if (rows.length === 0) return;

  try {
    await supabase.from('ai_chat_product_events').insert(rows);
  } catch {
    /* best-effort : le tracking ne doit jamais casser le chat */
  }
}

/** Raccourci : enregistre un unique événement. */
export function trackAiChatEvent(event: AiChatProductEventInput): void {
  void trackAiChatEvents([event]);
}

/**
 * Enregistre l'OUVERTURE de la fiche BOUTIQUE d'un vendeur depuis le chat IA.
 * Permet au marchand de voir, dans ses statistiques, quand un client a consulté
 * sa boutique via l'assistant IA. Best-effort : ne bloque jamais l'expérience.
 */
export function trackBoutiqueView(params: {
  merchantId: number;
  clientUserId?: number | null;
  clientName?: string | null;
  boutiqueName?: string | null;
  query?: string | null;
}): void {
  if (!params.merchantId) return;
  void trackAiChatEvents([{
    eventType: 'boutique',
    merchantId: params.merchantId,
    clientUserId: params.clientUserId ?? null,
    clientName: params.clientName ?? null,
    productName: params.boutiqueName || 'Boutique',
    query: params.query ?? null,
  }]);
}

/**
 * Notifie le marchand UNIQUEMENT quand le client ouvre réellement sa fiche
 * produit ou sa fiche boutique depuis le chat IA (plus à l'affichage dans les
 * résultats). Best-effort : ne bloque jamais l'expérience du chat.
 */
export function notifyMerchantOnAiClick(params: {
  userId: number;
  userName?: string | null;
  merchantId: number;
  itemName?: string | null;
  itemType: 'produit' | 'service' | 'boutique';
  query?: string | null;
}): void {
  if (!params.userId || !params.merchantId) return;
  void supabase.functions.invoke('zifek-client-chat', {
    body: {
      action: 'notify_click',
      userId: params.userId,
      userName: params.userName ?? null,
      merchantId: params.merchantId,
      itemName: params.itemName ?? null,
      itemType: params.itemType,
      query: params.query ?? null,
    },
  }).catch(() => {});
}